<template>
    <div class="select-text-wrapper">
        <select 
            ref="selectElement"
            :id="id"
            v-model="selectedValue" 
            class="select-text" 
            :style="{ width: selectWidth }"
            @change="onChange"
        >
            <option v-for="item in options" :value="item.value" :key="item.value">{{ item.label }}</option>
        </select>
        <ArrowDownSvg class="select-text-arrow" />
        <span ref="measureSpan" class="measure-span">{{ selectedLabel }}</span>
    </div>
</template>

<script>
import ArrowDownSvg from '@/assets/icons/arrow-down.svg';

export default {
    name: 'selectTextUI',
    components: {
        ArrowDownSvg
    },
    props: {
        id: {
            type: String,
            default: null
        },
        options: {
            type: Array,
            required: true,
            validator: (value) => {
                return value.every(item => 
                    typeof item === 'object' && 
                    'value' in item &&
                    'label' in item
                );
            }
        },
        defaultSelected: {
            type: [String, Number, null],
            default: null
        }
    },
    data() {
        return {
            selectedValue: this.defaultSelected,
            selectWidth: 'auto',
            minWidth: 20
        }
    },
    computed: {
        selectedLabel() {
            const selectedOption = this.options.find(opt => opt.value === this.selectedValue);
            return selectedOption ? selectedOption.label : '';
        }
    },
    watch: {
        defaultSelected(newVal) {
            this.selectedValue = newVal;
        },
        selectedValue() {
            this.$nextTick(() => {
                this.updateWidth();
            });
        },
        selectedLabel() {
            this.$nextTick(() => {
                this.updateWidth();
            });
        }
    },
    mounted() {
        // Use setTimeout to ensure DOM is fully rendered
        setTimeout(() => {
            this.updateWidth();
        }, 0);
    },
    methods: {
        onChange() {
            this.$emit('change', this.selectedValue);
            this.$nextTick(() => {
                this.updateWidth();
            });
        },
        updateWidth() {
            if (!this.$refs.measureSpan) return;
            
            // Ensure the span has the current label
            this.$refs.measureSpan.textContent = this.selectedLabel || '';
            
            // Force a reflow to ensure measurement is accurate
            void this.$refs.measureSpan.offsetWidth;
            
            const textWidth = this.$refs.measureSpan.offsetWidth;
            
            if (textWidth > 0) {
                // Add padding for arrow (24px right padding)
                this.selectWidth = (textWidth + 18) + 'px';
            } else if (this.options.length > 0) {
                // Fallback: use first option or measure all to get a reasonable width
                this.$refs.measureSpan.textContent = this.options[0].label;
                const fallbackWidth = this.$refs.measureSpan.offsetWidth;
                this.selectWidth = (fallbackWidth + 18) + 'px';
                // Restore current label
                this.$refs.measureSpan.textContent = this.selectedLabel || '';
            }
        }
    }
}
</script>

<style scoped>
.select-text-wrapper {
    position: relative;
    display: inline-block;
}

.select-text {
    min-width: 50px;
    padding: 2px var(--spacing-lg) 1px 0;
    font-family: var(--font-family-primary);
    font-size: var(--text-sm);
    line-height: 1;
    font-weight: 400;
    color: var(--color-secondary);
    background-color: transparent;
    border: none;
    border-bottom: 1px solid var(--border-primary);
    border-radius: 0;
    cursor: pointer;
    transition: border-color 0.2s ease-in-out;
    appearance: none;
    outline: none;
}

.select-text:hover {
    border-bottom-color: var(--color-primary);
}

.select-text:focus {
    border-bottom-color: var(--color-primary);
}

.select-text-arrow {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    width: 16px;
    height: 16px;
    color: var(--color-secondary);
    transition: transform 0.3s ease;
}

.select-text-wrapper:focus-within .select-text-arrow {
    transform: translateY(-50%) scaleY(-1);
}

.measure-span {
    position: absolute;
    left: 0;
    visibility: hidden;
    white-space: nowrap;
    font-family: var(--font-family-primary);
    font-size: var(--text-sm);
    font-weight: 400;
    line-height: 1;
    padding: 0;
}
</style>

